import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import {
  tcTokenLoseEfficacy,
  tcShowToast,
  isApp,
} from '@/utils/bridge'
import { getToken } from '@/utils/auth'
import { getApiBaseUrl } from '@/utils/env'
import { getDeviceType } from '@/utils'
import axios from 'axios'
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { captureException } from './sentry'
import { track } from '@/plugins/event-tracker'
import { signHmacSHA256, AesGcmUtil } from '@/utils/crypto'
import { getSearchParam } from '@/utils/urlQuery'

// 调试模式开关 - 控制调试日志输出和 VConsole 工具
const isDebugMode = import.meta.env.VITE_APP_ENV !== 'prod';

const SENSITIVE_DEBUG_KEYS = new Set([
  'accesstoken',
  'access_token',
  'authorization',
  'auth_request',
  'callbackurl',
  'refreshtoken',
  'refresh_token',
  'token',
])

const sanitizeDebugData = (_url: string, data: unknown): unknown => {
  if (typeof data === 'string') {
    return data.replace(
      /(^|&)(auth_request|access_token|refresh_token|token|authorization)=[^&]*/gi,
      '$1$2=[REDACTED]',
    )
  }
  if (!data || typeof data !== 'object') return data

  const sanitizeValue = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sanitizeValue)
    if (!value || typeof value !== 'object') return value
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SENSITIVE_DEBUG_KEYS.has(key.toLowerCase()) ? '[REDACTED]' : sanitizeValue(item),
      ]),
    )
  }
  return sanitizeValue(data)
}

// 日志输出函数
const debugLog = (
  type: string,
  url: string,
  data: unknown,
  skipDebugLog = false,
) => {
  if (isDebugMode && !skipDebugLog) {
    const timestamp = new Date().toLocaleString("zh-CN", {
      hour12: false,
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    console.log(`[${timestamp}] ${type} - ${url}`);
    console.log(sanitizeDebugData(url, data));
    console.log("═══════════════════════════════════════"); // 分隔线
  }
};

/**
 * 接口签名和加密密钥（后端提供）
 * 注意：生产环境应该从环境变量或配置中心获取，此处为演示写死
 */
const API_SECRET = import.meta.env.VITE_API_SECRET

/**
 * 加密配置
 */
const ENCRYPTION_CONFIG = {
  enabled: import.meta.env.VITE_ENABLE_ENCRYPTION
}

/**
 * 判断是否应该启用加密
 */
function shouldEnableEncryption(): boolean {
  const config = ENCRYPTION_CONFIG.enabled

  if (config === 'false') {
    // 强制禁用
    return false
  }

  if (config === 'true') {
    // 强制启用（如果环境不支持会报错）
    return true
  }

  // 'auto': 自动检测环境
  return AesGcmUtil.isSupported()
}

/**
 * 生成请求签名
 * @param method - HTTP 方法
 * @param path - 请求路径
 * @param timestamp - 时间戳
 * @param nonce - 随机数
 * @param body - 请求体（JSON 字符串）
 * @returns 签名字符串
 */
function generateSignature(
  method: string,
  path: string,
  timestamp: string,
  nonce: string,
  body?: string
): string {
  // 签名内容格式：METHOD\nPATH\nTIMESTAMP\nNONCE\nBODY
  const parts = [
    method.toUpperCase(),
    path,
    timestamp,
    nonce,
    body || ''
  ]
  const content = parts.join('\n')
  return signHmacSHA256(content, API_SECRET)
}

/**
 * 生成随机字符串
 * @param length - 字符串长度
 * @returns 随机字符串
 */
function generateNonce(length = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 获取来源类型
 *
 * App 环境：返回 ''
 * H5 环境：从 URL 参数 ?source=xxx 获取（如 /?source=douyin 或 /bill?source=xiaohongshu）
 */
function getSourceType(): string {
  try {
    // App 环境直接返回 ''
    if (isApp()) {
      return ''
    }

    // H5 环境：从 URL 参数获取 source
    const urlSource = getSearchParam('source')

    if (urlSource) {
      console.log(`[Request] 从 URL 获取 source: ${urlSource}`)
      return urlSource.toLowerCase()
    }

    // 没有 source 参数，返回空字符串
    return ''
  } catch (error) {
    console.error('[Request] 获取 sourceType 失败:', error)
    return ''
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * 跳过开发环境的请求和响应调试日志。
     * 适用于高频轮询等会持续刷屏、但仍需保留错误日志的请求。
     */
    skipDebugLog?: boolean

    /**
     * 静默模式：不显示错误 Toast 提示
     *
     * @description
     * 当设置为 true 时，请求失败不会弹出 Toast 提示，只会在控制台记录日志。
     * 适用场景：埋点上报、后台轮询等不需要打扰用户的请求。
     *
     * @example
     * ```ts
     * request.post('/api/endpoint', data, { silent: true })
     * ```
     *
     * @default false
     */
    silent?: boolean

    /** Skip the global token-expired UI for a request that handles 401 locally. */
    suppressTokenLoss?: boolean

    /** Do not derive the generic sourceType header from the current H5 URL. */
    skipUrlSourceType?: boolean
    /**
     * 跳过错误埋点：防止埋点接口失败导致死循环
     *
     * @description
     * 当设置为 true 时，请求失败不会触发错误埋点上报。
     * 适用场景：埋点上报接口（防止埋点接口失败 → 上报错误埋点 → 再次失败 → 死循环）。
     *
     * @example
     * ```ts
     * request.post('/api/eventlog', data, { skipErrorReport: true })
     * ```
     *
     * @default false
     */
    skipErrorReport?: boolean

    /**
     * 加密请求体：使用 AES-256-GCM 加密敏感数据
     *
     * @description
     * 默认情况下，所有 POST/PUT/PATCH 请求体会使用 AES-256-GCM 加密。
     * 设置为 false 可以跳过加密（如文件上传等特殊场景）。
     *
     * @example
     * ```ts
     * // 默认加密
     * request.post('/api/user/login', { username, password })
     *
     * // 跳过加密
     * request.post('/api/public/data', data, { encrypt: true })
     * ```
     *
     * @default true
     */
    encrypt?: boolean
  }
}
// 定义响应数据结构
interface ApiResponse<T = unknown> {
  status: number
  message?: string
  data: T
}

// 文件上传配置选项
interface UploadConfig {
  // 上传进度回调
  onUploadProgress?: (progressEvent: { loaded: number; total?: number; progress?: number }) => void
  // 中断信号
  signal?: AbortSignal
  // 其他 axios 配置
  [key: string]: unknown
}

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(), // 动态获取API地址
  timeout: 15000, // 普通请求 超时时间设置为 15 秒
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
})

// 请求拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getToken()

    if (token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token}`
    }

    // 生成时间戳和随机数
    const timestamp = Date.now().toString()
    const nonce = generateNonce()

    config.headers['X-Request-Timestamp'] = timestamp
    config.headers['X-Request-Nonce'] = nonce

    // 检查加密能力（只调用一次）
    const encryptionSupported = shouldEnableEncryption()

    // 添加加密能力协商头（告诉后端客户端是否支持加密）
    config.headers['X-Client-Encryption-Capable'] = encryptionSupported ? 'true' : 'false'

    if (!encryptionSupported) {
      console.warn('[Request] 加密功能已禁用或不支持，请求将以明文发送')
    }

    // 添加设备信息到 headers（App 环境）
    if (isApp()) {
      const appStore = useAppStore()
      const deviceInfo = appStore.getDeviceInfo()

      if (deviceInfo) {
        config.headers['deviceType'] = deviceInfo.deviceType
        // clientVersion 二次校验：bridge 已 normalize 过一次，
        // 这里再校验一次防其他路径写入非法值（如 'UNSUPPORTED'）
        const cv = deviceInfo.clientVersion
        config.headers['clientVersion'] = (typeof cv === 'string' && /^\d+\.\d+(\.\d+)?([.\-+][\w.-]*)?$/.test(cv.trim()))
          ? cv.trim()
          : '0.0.0'
        config.headers['appChannel'] = deviceInfo.appChannel
        config.headers['deviceId'] = deviceInfo.deviceId
        config.headers['sourceType'] = deviceInfo.sourceType
      }
    } else {
      // 浏览器时候 也要加上 deviceType和appChannel和sourceType，需要h5自己取，目前跟后端约定先写死的
      const deviceType = getDeviceType()
      const appChannel = 'h5'
      const sourceType = config.skipUrlSourceType ? '' : getSourceType()
      config.headers['deviceType'] = deviceType
      config.headers['appChannel'] = appChannel
      config.headers['sourceType'] = sourceType || ''
    }

    // 处理请求体（加密 + 签名）
    const method = config.method || 'get'
    const path = config.url || ''
    let body = ''

    // 如果有请求体，处理加密和序列化
    if (config.data && ['post', 'put', 'patch'].includes(method.toLowerCase())) {
      const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData
      const isBlob = typeof Blob !== 'undefined' && config.data instanceof Blob

      if (isFormData || isBlob) {
        // multipart / binary 走 axios 默认链路，不 JSON 化也不加密；body 留空走 GET 同口径签名
        body = ''
      } else {
        // 记录加密前的业务入参
        debugLog(
          `📤 ${method.toUpperCase()} 请求 - 业务入参(未加密)`,
          path,
          config.data,
          config.skipDebugLog,
        )

        // 判断是否应该加密（使用前面缓存的值，避免重复调用）
        const shouldEncrypt = encryptionSupported && config.encrypt !== false

        if (shouldEncrypt) {
          try {
            // 加密：先序列化再加密
            const plaintext = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)
            const encrypted = await AesGcmUtil.encrypt(plaintext, API_SECRET)

            // 加密后的数据包装成对象
            config.data = { data: encrypted }
            config.headers['X-Request-Encrypted'] = 'true'

            // 用于签名的 body（加密后的）
            body = JSON.stringify(config.data)
          } catch (error) {
            console.error('[Request] 加密失败，降级为不加密:', error)
            // 加密失败，降级为不加密
            body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)
          }
        } else {
          // 不加密：直接序列化
          body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)
        }
      }
    }

    // 生成请求签名（对最终的 body 进行签名）
    const signature = generateSignature(method, path, timestamp, nonce, body)
    config.headers['X-Request-Signature'] = signature

    return config
  },
  (error) => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  },
)

// 响应拦截器
service.interceptors.response.use(
  async (response: AxiosResponse<ApiResponse>) => {
    let res = response.data

    // 如果客户端启用了加密，且响应数据是字符串，则尝试解密
    // 后端会根据请求头 X-Client-Encryption-Capable 来决定是否加密响应
    const encryptionEnabled = shouldEnableEncryption()

    if (encryptionEnabled && res.data && typeof res.data === 'string') {
      try {
        // 解密响应数据
        const decrypted = await AesGcmUtil.decrypt(res.data, API_SECRET)
        // 解密后是完整的响应对象 {status, data, message}
        res = JSON.parse(decrypted)
        debugLog(
          '📥 响应 - 业务出参(已解密)',
          response.config.url || '',
          res,
          response.config.skipDebugLog,
        )
      } catch (error) {
        console.error('[Response] 解密失败:', error)
        return Promise.reject({
          status: 500,
          message: '数据解密失败',
          data: null
        })
      }
    } else {
      debugLog(
        '📥 响应 - 业务出参(未加密)',
        response.config.url || '',
        res,
        response.config.skipDebugLog,
      )
    }

    // MCP authorization uses a resource-shaped success body rather than the
    // numeric business envelope used by ordinary APIs.
    if (response.config?.url === '/mcpfacade/oauth/complete' && String(res.status) === 'authorized') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return res as any
    }

    // 判断业务状态码
    if (res.status === 200) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return res.data as any
    }

    // 业务错误弹出提示
    const isSilent = response.config?.silent === true
    // [debug] Bug 0 跟踪：silent 是否透传成功 + 业务错误码/消息
    console.warn('[request] biz error', {
      url: response.config?.url,
      status: res.status,
      isSilent,
      message: res.message,
    })
    if (!isSilent) {
      tcShowToast(res.message || '请求失败')
    }
    return Promise.reject(res)
  },
  async (error) => {
    if (error.config?.url === '/mcpfacade/oauth/complete' && error.config.data) {
      error.config.data = 'auth_request=[REDACTED]'
    }
    const isSilent = error.config?.silent === true
    const skipErrorReport = error.config?.skipErrorReport === true
    // [debug] Bug 0 跟踪：axios reject 分支 silent 是否透传 + 错误码/消息
    console.warn('[request] axios error', {
      url: error.config?.url,
      httpStatus: error.response?.status,
      bizStatus: error.response?.data?.status,
      isSilent,
      message: error.response?.data?.message || error.message,
    })

    // 错误响应解密（与 success 分支同口径）：后端 4xx/5xx 业务错误体也会被加密，
    // 必须先解开才能拿到 status/message/data 给上层 useDraftBillError 兜底
    if (error.response?.data) {
      try {
        const encryptionEnabled = shouldEnableEncryption()
        const raw = error.response.data
        if (encryptionEnabled && raw && typeof raw.data === 'string') {
          const decrypted = await AesGcmUtil.decrypt(raw.data, API_SECRET)
          error.response.data = JSON.parse(decrypted)
        }
      } catch (e) {
        console.error('[Response] 错误响应解密失败:', e)
      }
    }

    // 记录请求开始时间（从 headers 中获取）
    const requestStartTime = parseInt(error.config?.headers?.['X-Request-Timestamp'] || '0')
    const apiDuration = requestStartTime ? Date.now() - requestStartTime : 0

    // 提取通用埋点参数
    const getCommonErrorParams = () => {
      const url = error.config?.url || ''
      let apiPath = url
      let apiHost = ''

      try {
        // 尝试构造完整 URL
        const baseURL = error.config?.baseURL || window.location.origin
        const apiUrl = new URL(url, baseURL)
        apiPath = apiUrl.pathname
        apiHost = apiUrl.host
      } catch {
        // URL 构造失败，使用原始 url
        apiPath = url
        apiHost = window.location.host
      }

      return {
        api_path: apiPath,
        http_method: (error.config?.method || '').toUpperCase(),
        api_host: apiHost,
        api_duration: apiDuration,
        request_id: error.response?.headers?.['x-request-id'] || error.config?.headers?.['X-Request-Id'] || '',
      }
    }

    // 处理 HTTP 错误
    if (error.response) {
      const httpCode = error.response.status

      // 上报错误埋点
      if (!skipErrorReport) {
        let exceptionType = 'api'

        if (httpCode === 401) {
          exceptionType = 'auth'
        } else if (httpCode >= 500) {
          exceptionType = 'api'
        } else if (httpCode >= 400) {
          exceptionType = 'logic'
        }

        track('api_error', {
          ...getCommonErrorParams(),
          http_code: httpCode,
          error_type: 'api_error',
          exception_type: exceptionType,
          error_message: error.response.data?.message || error.response.statusText || 'HTTP错误',
        })
      }

      switch (error.response.status) {
        case 401:
          const userStore = useUserStore()
          userStore.clearAuth()
          if (error.config?.suppressTokenLoss !== true) {
            tcTokenLoseEfficacy()
          }
          break
        case 403:
          console.error('拒绝访问')
          break
        case 404:
          console.error('请求地址不存在')
          break
        case 500:
          console.error('服务器错误')
          break
        default:
          console.error(`请求失败: ${error.response.status}`)
      }

      // 上报 5xx 错误到 Sentry（统一处理）
      if (error.response.status >= 500) {
        captureException(error, {
          request: {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response.status,
            statusText: error.response.statusText,
          },
        })
      }
      return Promise.reject(error)
    }

    // 网络错误统一处理
    let errorMessage = '网络错误，请检查网络连接'
    let errorType = 'network'
    let exceptionType = 'network'
    let cancleFlag = false
    if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
      errorMessage = '网络异常，请检查网络连接'
      errorType = 'no_network'
      exceptionType = 'network'
    } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_TIMEOUT' || error.message?.toLowerCase().includes('timeout')) {
      errorMessage = '请求超时，请稍后重试'
      errorType = 'timeout'
      exceptionType = 'network'
    } else if (error.code === 'ERR_CANCELED') {
      cancleFlag = true
      errorType = 'cancelled'
      exceptionType = 'other'
    } else if (error.message?.toLowerCase().includes('dns')) {
      errorType = 'dns_fail'
      exceptionType = 'network'
    } else if (error.message?.toLowerCase().includes('ssl') || error.message?.toLowerCase().includes('certificate')) {
      errorType = 'ssl_error'
      exceptionType = 'network'
    } else if (error.message?.toLowerCase().includes('parse') || error.message?.toLowerCase().includes('json')) {
      errorType = 'parse_error'
      exceptionType = 'logic'
    } else if (error.message) {
      errorMessage = error.message
    }

    // 上报网络错误埋点
    if (!skipErrorReport && errorType !== 'cancelled') {
      track('api_error', {
        ...getCommonErrorParams(),
        http_code: 0,
        error_type: errorType,
        exception_type: exceptionType,
        error_message: errorMessage,
      })
    }

    // 修改 error.message 为中文，这样业务层 catch 也能拿到中文
    error.message = errorMessage

    // 上报网络错误到 Sentry（仅非超时错误，超时可能是正常情况）
    if (error.code !== 'ECONNABORTED' && error.code !== 'ERR_TIMEOUT') {
      captureException(error, {
        network: {
          code: error.code,
          message: errorMessage,
          url: error.config?.url,
          method: error.config?.method,
        },
      })
    }

    if (!isSilent) {
      if (!cancleFlag) {
        tcShowToast(errorMessage)
      }
    }

    return Promise.reject(error)
  },
)

// 创建类型安全的请求方法
interface RequestInstance {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(url: string, config?: any): Promise<T>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any>(url: string, data?: any, config?: any): Promise<T>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any>(url: string, data?: any, config?: any): Promise<T>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any>(url: string, config?: any): Promise<T>
}

/**
 * 文件上传方法
 * @param url 上传地址
 * @param file 文件对象 (File | Blob)
 * @param fieldName 文件字段名，默认为 'file'
 * @param data 额外的表单数据
 * @param config 上传配置（包含进度回调等）
 * @returns Promise<T>
 */
export const uploadFile = async <T = unknown>(
  url: string,
  file: File | Blob,
  fieldName = 'file',
  data?: Record<string, unknown>,
  config?: UploadConfig,
): Promise<T> => {
  const formData = new FormData()
  formData.append(fieldName, file)

  // 添加额外的表单数据
  if (data) {
    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (value !== undefined && value !== null) {
        formData.append(key, value as string | Blob)
      }
    })
  }

  return service.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 20000, // 文件上传超时时间设置为 20 秒
    ...config,
  }) as Promise<T>
}

export default service as unknown as RequestInstance
